<script setup>
import { ref, computed, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'shared/components/Spinner.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();

const STEPS = { CREDENTIALS: 'credentials', QR_CODE: 'qr_code' };

const step = ref(STEPS.CREDENTIALS);
const instanceUrl = ref('');
const apiToken = ref('');
const isConnecting = ref(false);
const qrCodeData = ref('');
const qrRefreshTimer = ref(null);
const instanceConnected = ref(false);
const connectionPhone = ref('');
const inboxName = ref('');
const isCreating = ref(false);

const isCreatingInbox = computed(() => isCreating.value);
const connectionError = ref('');

function proxyBaseUrl() {
  const accountId = window.location.pathname.split('/')[3];
  return `/api/v1/accounts/${accountId}/uazapi_proxy`;
}

function cleanInstanceUrl() {
  return instanceUrl.value.replace(/\/+$/, '');
}

function detectConnected(data) {
  // uazapi returns: { instance: { status: "connected" }, status: { connected: true } }
  // We need to check string fields first, skip objects
  const rawState =
    data.state ||
    data.instance?.state ||
    data.instance?.status ||
    data.data?.state ||
    data.data?.status ||
    '';
  const state = typeof rawState === 'string' ? rawState.toLowerCase() : '';

  if (
    state === 'connected' ||
    state === 'open' ||
    state === 'online' ||
    data.connected === true ||
    data.status?.connected === true ||
    data.status?.loggedIn === true
  ) {
    instanceConnected.value = true;
    connectionPhone.value =
      data.phone ||
      data.instance?.owner ||
      data.instance?.phone ||
      data.data?.phone ||
      data.jid ||
      data.status?.jid?.split(':')[0] ||
      data.instance?.wuid ||
      data.data?.jid ||
      '';
    stopQrRefresh();
    return true;
  }
  return false;
}

// Proxy through Chatwoot backend to avoid CORS issues
async function fetchQrCode() {
  try {
    const { data } = await axios.post(`${proxyBaseUrl()}/connect`, {
      instance_url: cleanInstanceUrl(),
      api_token: apiToken.value,
    });

    // Check if already connected from connect response
    if (detectConnected(data)) return;

    const qr =
      data.qrcode ||
      data.instance?.qrcode ||
      data.base64 ||
      data.qr ||
      data.code ||
      data.data?.qrcode ||
      data.data?.base64 ||
      data.data?.qr ||
      data.instance?.base64 ||
      '';
    if (qr) {
      qrCodeData.value = qr;
    }
  } catch {
    // QR fetch failed, will retry on next interval
  }
}

async function checkConnection() {
  try {
    const { data } = await axios.get(`${proxyBaseUrl()}/status`, {
      params: {
        instance_url: cleanInstanceUrl(),
        api_token: apiToken.value,
      },
    });
    detectConnected(data);
  } catch {
    // Status check failed
  }
}

function startQrRefresh() {
  fetchQrCode();
  qrRefreshTimer.value = setInterval(async () => {
    await checkConnection();
    if (!instanceConnected.value) {
      await fetchQrCode();
    }
  }, 15000);
}

function stopQrRefresh() {
  if (qrRefreshTimer.value) {
    clearInterval(qrRefreshTimer.value);
    qrRefreshTimer.value = null;
  }
}

async function handleConnect() {
  if (!instanceUrl.value || !apiToken.value) return;

  isConnecting.value = true;
  connectionError.value = '';
  try {
    // First try to check if already connected
    await checkConnection();
    if (instanceConnected.value) {
      step.value = STEPS.QR_CODE;
      isConnecting.value = false;
      return;
    }

    // Try to fetch QR code to validate connection
    await fetchQrCode();
    step.value = STEPS.QR_CODE;
    startQrRefresh();
  } catch {
    connectionError.value = t(
      'INBOX_MGMT.ADD.WHATSAPP.UAZAPI.CONNECTION_ERROR'
    );
    useAlert(t('INBOX_MGMT.ADD.WHATSAPP.UAZAPI.CONNECTION_ERROR'));
  } finally {
    isConnecting.value = false;
  }
}

async function configureUazapiChatwoot(inboxId) {
  await window.axios.put(`${proxyBaseUrl()}/configure_chatwoot`, {
    chatwoot_inbox_id: inboxId,
    sign_messages: false,
    ignore_groups: false,
    create_new_conversation: true,
  });
}

async function createInbox() {
  if (!inboxName.value) return;
  isCreating.value = true;

  try {
    const response = await store.dispatch('inboxes/createChannel', {
      name: inboxName.value.trim(),
      channel: {
        type: 'whatsapp',
        phone_number: `+${(connectionPhone.value || '0000000000').replace(/^\+/, '')}`,
        provider: 'uazapi',
        provider_config: {
          api_key: apiToken.value,
          instance_url: instanceUrl.value.replace(/\/+$/, ''),
        },
      },
    });

    // Configure uazapi Chatwoot integration automatically
    try {
      await configureUazapiChatwoot(response.id);
    } catch {
      // Non-blocking: inbox was created, integration config can be retried
    }

    router.replace({
      name: 'settings_inboxes_add_agents',
      params: { page: 'new', inbox_id: response.id },
    });
  } catch (error) {
    useAlert(
      error.message || t('INBOX_MGMT.ADD.WHATSAPP.API.ERROR_MESSAGE')
    );
  } finally {
    isCreating.value = false;
  }
}

onBeforeUnmount(() => {
  stopQrRefresh();
});
</script>

<template>
  <div>
    <!-- Step 1: Credentials -->
    <div v-if="step === STEPS.CREDENTIALS">
      <div class="flex items-center gap-3 mb-6">
        <div
          class="flex items-center justify-center size-12 rounded-full bg-gradient-to-br from-green-500 to-emerald-600"
        >
          <Icon icon="i-lucide-message-circle" class="size-6 text-white" />
        </div>
        <div>
          <h2 class="text-base font-semibold text-n-slate-12 m-0">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.UAZAPI.TITLE') }}
          </h2>
          <p class="text-sm text-n-slate-11 m-0">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.UAZAPI.DESCRIPTION') }}
          </p>
        </div>
      </div>

      <form class="flex flex-col gap-4" @submit.prevent="handleConnect">
        <label class="flex flex-col gap-1.5">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.UAZAPI.INSTANCE_URL.LABEL') }}
          </span>
          <input
            v-model="instanceUrl"
            type="url"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-blue-7"
            :placeholder="
              $t('INBOX_MGMT.ADD.WHATSAPP.UAZAPI.INSTANCE_URL.PLACEHOLDER')
            "
            required
          />
          <span class="text-xs text-n-slate-10">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.UAZAPI.INSTANCE_URL.HINT') }}
          </span>
        </label>

        <label class="flex flex-col gap-1.5">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.UAZAPI.API_TOKEN.LABEL') }}
          </span>
          <input
            v-model="apiToken"
            type="password"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-blue-7"
            :placeholder="
              $t('INBOX_MGMT.ADD.WHATSAPP.UAZAPI.API_TOKEN.PLACEHOLDER')
            "
            required
          />
        </label>

        <div class="mt-2">
          <NextButton
            :is-loading="isConnecting"
            type="submit"
            solid
            blue
            :label="$t('INBOX_MGMT.ADD.WHATSAPP.UAZAPI.CONNECT_BUTTON')"
          />
        </div>
        <p
          v-if="connectionError"
          class="mt-2 text-sm text-n-ruby-11"
        >
          {{ connectionError }}
        </p>
      </form>
    </div>

    <!-- Step 2: QR Code -->
    <div v-else-if="step === STEPS.QR_CODE">
      <div class="flex flex-col items-center text-center">
        <!-- Connected state -->
        <div v-if="instanceConnected" class="flex flex-col items-center gap-4">
          <div
            class="flex items-center justify-center size-16 rounded-full bg-green-100 dark:bg-green-900/30"
          >
            <Icon
              icon="i-lucide-check-circle"
              class="size-8 text-green-600 dark:text-green-400"
            />
          </div>
          <div>
            <h3 class="text-lg font-semibold text-n-slate-12 m-0">
              {{ $t('INBOX_MGMT.ADD.WHATSAPP.UAZAPI.CONNECTED_TITLE') }}
            </h3>
            <p
              v-if="connectionPhone"
              class="text-sm text-n-slate-11 mt-1 m-0"
            >
              {{ connectionPhone }}
            </p>
          </div>

          <form
            class="w-full max-w-sm mt-4 flex flex-col gap-4"
            @submit.prevent="createInbox"
          >
            <label class="flex flex-col gap-1.5 text-left">
              <span class="text-sm font-medium text-n-slate-12">
                {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.LABEL') }}
              </span>
              <input
                v-model="inboxName"
                type="text"
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-blue-7"
                :placeholder="
                  $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.PLACEHOLDER')
                "
                required
              />
            </label>
            <NextButton
              :is-loading="isCreatingInbox"
              type="submit"
              solid
              blue
              :label="$t('INBOX_MGMT.ADD.WHATSAPP.UAZAPI.CREATE_INBOX_BUTTON')"
            />
          </form>
        </div>

        <!-- QR Code scanning state -->
        <div v-else class="flex flex-col items-center gap-4">
          <div class="flex items-center gap-2">
            <Icon
              icon="i-lucide-scan-line"
              class="size-5 text-n-slate-11"
            />
            <h3 class="text-lg font-semibold text-n-slate-12 m-0">
              {{ $t('INBOX_MGMT.ADD.WHATSAPP.UAZAPI.QR_TITLE') }}
            </h3>
          </div>
          <p class="text-sm text-n-slate-11 m-0 max-w-sm">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.UAZAPI.QR_DESCRIPTION') }}
          </p>

          <div
            v-if="qrCodeData"
            class="mt-2 p-4 bg-white rounded-xl border border-n-weak shadow-sm"
          >
            <img
              :src="
                qrCodeData.startsWith('data:')
                  ? qrCodeData
                  : `data:image/png;base64,${qrCodeData}`
              "
              alt="QR Code"
              class="size-64"
            />
          </div>
          <div
            v-else
            class="mt-2 flex items-center justify-center size-64 bg-n-alpha-2 rounded-xl"
          >
            <Spinner />
          </div>

          <div
            class="flex items-center gap-2 mt-2 px-3 py-1.5 rounded-full bg-n-alpha-2 text-xs text-n-slate-11"
          >
            <Icon icon="i-lucide-refresh-cw" class="size-3" />
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.UAZAPI.QR_AUTO_REFRESH') }}
          </div>

          <div
            class="mt-4 text-left w-full max-w-sm bg-n-alpha-1 rounded-lg p-4"
          >
            <p class="text-sm font-medium text-n-slate-12 m-0 mb-2">
              {{ $t('INBOX_MGMT.ADD.WHATSAPP.UAZAPI.HOW_TO_CONNECT') }}
            </p>
            <ol class="text-sm text-n-slate-11 m-0 pl-4 flex flex-col gap-1">
              <li>
                {{ $t('INBOX_MGMT.ADD.WHATSAPP.UAZAPI.STEP_1') }}
              </li>
              <li>
                {{ $t('INBOX_MGMT.ADD.WHATSAPP.UAZAPI.STEP_2') }}
              </li>
              <li>
                {{ $t('INBOX_MGMT.ADD.WHATSAPP.UAZAPI.STEP_3') }}
              </li>
              <li>
                {{ $t('INBOX_MGMT.ADD.WHATSAPP.UAZAPI.STEP_4') }}
              </li>
            </ol>
          </div>

          <button
            class="mt-4 text-sm text-n-slate-11 hover:text-n-slate-12 underline cursor-pointer"
            @click="step = STEPS.CREDENTIALS"
          >
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.UAZAPI.BACK_BUTTON') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
